import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Input } from "@/components/ui/input";

import { Conversion } from "@/types/dashboard.types";
import { pdf } from "@react-pdf/renderer";
import {
  Check,
  Eye,
  Flag,
  GraduationCap,
  Info,
  Loader2,
  Pencil,
  Printer,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import slugify from "slugify";
import { useUpdateConversion } from "../queries/conversion.update.queries";
import { Template } from "./pdf-template";
import { toast } from "sonner";

export function EvaluationTable({
  data: { conversion, studentInformation, documentId },
  countryId,
  gradingScaleId,
  fileUrl,
  batchId,
}: {
  data: Conversion & { documentId: string };
  fileUrl: string | null;
  countryId: string;
  gradingScaleId: string;
  batchId: string;
}) {
  const hasCredits = conversion.table.some((item) => item.credits);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState(conversion.table);
  const { mutateAsync: updateConversion, isPending: isUpdating } =
    useUpdateConversion({
      documentId,
      batchId,
      conversion: {
        table: editedData,
        conclusion: conversion.conclusion,
        cumulativeGPA: conversion.cumulativeGPA,
      },
      countryId,
      gradingScaleId,
    });

  const handleEdit = () => {
    setIsEditMode(true);
    setEditedData([...conversion.table]);
  };

  const handleSave = async () => {
    if (editedData.some((item) => Number(item.credits) < 1)) {
      toast.error("Credits cannot be less than 1");
      return;
    }

    await updateConversion();

    setIsEditMode(false);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedData([...conversion.table]);
  };

  const handleCellChange = (index: number, field: string, value: string) => {
    const newData = [...editedData];
    if (field === "subject") {
      newData[index].subject = value;
    } else if (field === "score") {
      newData[index].score = value;
    } else if (field === "credits") {
      newData[index].credits = value;
    }
    setEditedData(newData);
  };

  const displayData = isEditMode ? editedData : conversion.table;

  return (
    <div className="flex flex-col gap-4">
      {studentInformation.name && (
        <h1 className="text-base">{studentInformation.name}&apos;s Report</h1>
      )}
      <Card className="w-full">
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="text-sm">
                <TableHead colSpan={2}>Subject Name</TableHead>
                <TableHead>Subject Grade</TableHead>
                {hasCredits && (
                  <TableHead className="text-center">Credits</TableHead>
                )}
                <TableHead className="text-right">US Grade Points</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayData.map((item, index) => (
                <TableRow className="text-sm py-2 h-[3rem]" key={index}>
                  <TableCell colSpan={2} className="font-bold">
                    {isEditMode ? (
                      <Input
                        value={item.subject}
                        onChange={(e) =>
                          handleCellChange(index, "subject", e.target.value)
                        }
                        className="font-bold"
                      />
                    ) : (
                      item.subject
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditMode ? (
                      <Input
                        value={item.score}
                        onChange={(e) =>
                          handleCellChange(index, "score", e.target.value)
                        }
                      />
                    ) : (
                      item.score
                    )}
                  </TableCell>
                  {hasCredits && (
                    <TableCell className="text-center">
                      {isEditMode ? (
                        <Input
                          value={item.credits ?? "1"}
                          onChange={(e) =>
                            handleCellChange(index, "credits", e.target.value)
                          }
                          type="number"
                          step="0.1"
                          className="text-center"
                        />
                      ) : (
                        item.credits ?? 1
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">{item.points}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="text-sm">
                <TableCell colSpan={hasCredits ? 4 : 3} className="font-bold">
                  Total GPA
                </TableCell>
                <TableCell className="text-right">
                  {conversion.cumulativeGPA}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
        <CardFooter className="flex justify-end w-full gap-2">
          {!isEditMode && (
            <div className="flex gap-2 justify-end">
              <Button
                onClick={async () => {
                  const blob = await pdf(
                    <Template
                      props={{
                        conversion: conversion,
                        studentInformation: studentInformation,
                      }}
                    />
                  ).toBlob();

                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `${slugify(
                    studentInformation.name
                      ? studentInformation.name
                      : "manual-conversion"
                  )}-transcript.pdf`;
                  link.click();
                }}
              >
                <Printer size={16} />
                Download Transcript
              </Button>
              {studentInformation.name && (
                <MoreInfo
                  conversion={conversion}
                  studentInformation={studentInformation}
                />
              )}
              {fileUrl && (
                <Button
                  variant={"outline"}
                  onClick={() => {
                    window.open(fileUrl, "_blank");
                  }}
                >
                  <Eye size={16} />
                  View Transcript
                </Button>
              )}
            </div>
          )}
          <div className="flex gap-2">
            {!isEditMode ? (
              <Button variant="outline" onClick={handleEdit}>
                <Pencil size={16} />
                Edit
              </Button>
            ) : (
              <>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  {isUpdating ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  <X size={16} />
                  Cancel
                </Button>
              </>
            )}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

function MoreInfo({
  conversion,
  studentInformation,
}: {
  conversion: Conversion["conversion"];
  studentInformation: Conversion["studentInformation"];
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="w-fit gap-2 ml-auto" variant={"outline"}>
          <Info size={16} />
          More Info
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {studentInformation.name}&apos;s Report
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Card>
            <CardContent>
              <div className="grid grid-cols-[25px_1fr] items-start gap-x-4 gap-y-3 text-sm">
                <User className="h-5 w-5 text-primary" />
                <div className="font-semibold">{studentInformation.name}</div>
                <Flag className="h-5 w-5 text-primary" />
                <div>{studentInformation.country}</div>
                <GraduationCap className="h-5 w-5 text-primary" />
                <div>Grade : {studentInformation.grade}</div>
                <GraduationCap className="h-5 w-5 text-primary invisible" />
                <div>Curriculum : {studentInformation.scale}</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <h3 className="mb-2 font-semibold">Conclusion</h3>
              <p className="text-sm text-muted-foreground">
                {conversion.conclusion}
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
